/**
 * Sentry ingest for browser bundles — the three dashboard SPAs and the player
 * (dwf-007 §7.8; decision-log D30: "the DSN is configuration each SPA and the player reads").
 *
 * Why this is hand-written rather than `@sentry/browser`:
 *
 * - The player bundle is vanilla TypeScript with no framework and no runtime dependency
 *   (CLAUDE.md, /player). `@sentry/browser` is ~30 kB gzipped of machinery — instrumentation
 *   hooks, breadcrumb collectors, integrations — for one requirement: an event reaches a
 *   Sentry project. This module is the whole of that requirement and nothing else.
 * - Invariant 7: the player must never block first frame on the network. `initErrorReporting`
 *   performs no I/O at all — it parses a DSN and registers two listeners. The first byte
 *   leaves the device only when something has already gone wrong.
 * - Invariant 9: nothing in here may put an error on a customer-facing TV. Every failure path
 *   — missing DSN, malformed DSN, missing environment tag, no `fetch`, a 429 from ingest —
 *   ends in a counted, named, logged no-op. Nothing throws out of a capture call.
 *
 * The wire format is Sentry's envelope endpoint, which is a public, stable HTTP contract:
 * `POST {origin}{path}/api/{project_id}/envelope/?sentry_key={public_key}&sentry_version=7`
 * with a newline-delimited body of `envelope header \n item header \n item payload`.
 * Authentication rides in the query string rather than an `X-Sentry-Auth` header so that a
 * browser sends one preflight and not two.
 *
 * D195: a disabled reporter is not a silent one. It says which of the four reasons disabled
 * it, it says so on the console at init, `enabled` is `false` where anyone can read it, and
 * every dropped capture is counted in `stats().dropped`. There is no state in this file that
 * reports success it did not have.
 */

export const SDK_NAME = 'dw.observability';
export const SDK_VERSION = '0.1.0';

/** Sentry's own level vocabulary; not ours to rename. */
export type SentryLevel = 'fatal' | 'error' | 'warning' | 'info' | 'debug';

// ---------------------------------------------------------------------------
// DSN
// ---------------------------------------------------------------------------

export class SentryDsnError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'SentryDsnError';
  }
}

export interface SentryDsn {
  readonly publicKey: string;
  readonly projectId: string;
  readonly host: string;
  /** Fully-formed ingest URL including the `sentry_key` query string. */
  readonly envelopeUrl: string;
}

/**
 * Parse `https://<public key>@<host>[/<path prefix>]/<project id>`.
 *
 * Throws rather than returning a default: a DSN that cannot be parsed has no safe reading,
 * and `initErrorReporting` catches this to disable itself loudly instead of propagating.
 */
export function parseSentryDsn(dsn: string): SentryDsn {
  const trimmed = dsn.trim();
  if (trimmed === '') throw new SentryDsnError('DSN is empty');

  let url: URL;
  try {
    url = new URL(trimmed);
  } catch {
    throw new SentryDsnError('DSN is not a URL');
  }

  if (url.protocol !== 'https:' && url.protocol !== 'http:') {
    throw new SentryDsnError(`DSN scheme must be http or https, got "${url.protocol}"`);
  }

  const publicKey = url.username;
  if (publicKey === '') {
    throw new SentryDsnError('DSN has no public key (expected https://<key>@<host>/<project id>)');
  }
  if (url.password !== '') {
    // Legacy DSNs carried `public:secret`. A secret key baked into a browser bundle is a
    // credential published to every viewer; refuse it rather than ship it (OWNERSHIP.md §4).
    throw new SentryDsnError('DSN carries a secret key; browser bundles take the public DSN only');
  }

  const segments = url.pathname.split('/').filter((segment) => segment !== '');
  const projectId = segments.pop();
  if (projectId === undefined || !/^\d+$/.test(projectId)) {
    throw new SentryDsnError('DSN has no numeric project id in its path');
  }

  const prefix = segments.length > 0 ? `/${segments.join('/')}` : '';
  const port = url.port === '' ? '' : `:${url.port}`;
  const base = `${url.protocol}//${url.hostname}${port}${prefix}/api/${projectId}/envelope/`;
  const query = new URLSearchParams({
    sentry_key: publicKey,
    sentry_version: '7',
    sentry_client: `${SDK_NAME}/${SDK_VERSION}`,
  });

  return {
    publicKey,
    projectId,
    host: url.hostname,
    envelopeUrl: `${base}?${query.toString()}`,
  };
}

// ---------------------------------------------------------------------------
// Transport
// ---------------------------------------------------------------------------

export interface TransportRequest {
  readonly url: string;
  readonly body: string;
  readonly headers: Readonly<Record<string, string>>;
}

/** Rejects on any non-delivery. The reporter counts the rejection; it never rethrows. */
export type SentryTransport = (request: TransportRequest) => Promise<void>;

export function createFetchTransport(fetchImpl: typeof globalThis.fetch): SentryTransport {
  return async (request: TransportRequest): Promise<void> => {
    const response = await fetchImpl(request.url, {
      method: 'POST',
      headers: { ...request.headers },
      body: request.body,
      // The tab may be closing when the last error fires; `keepalive` lets the request
      // outlive the document. Envelopes are well under the 64 kB keepalive ceiling.
      keepalive: true,
      mode: 'cors',
      credentials: 'omit',
    });
    if (!response.ok) {
      throw new Error(`sentry ingest responded ${String(response.status)}`);
    }
  };
}

// ---------------------------------------------------------------------------
// Event shape
// ---------------------------------------------------------------------------

export interface SentryStackFrame {
  readonly filename: string;
  readonly function: string;
  readonly lineno: number;
  readonly colno: number;
  readonly in_app: boolean;
}

export interface SentryExceptionValue {
  readonly type: string;
  readonly value: string;
  readonly mechanism: { readonly type: string; readonly handled: boolean };
  readonly stacktrace?: { readonly frames: readonly SentryStackFrame[] };
}

export interface SentryEvent {
  readonly event_id: string;
  readonly timestamp: number;
  readonly platform: 'javascript';
  readonly level: SentryLevel;
  readonly logger: string;
  readonly environment: string;
  readonly tags: Readonly<Record<string, string>>;
  readonly sdk: { readonly name: string; readonly version: string };
  readonly release?: string;
  readonly extra?: Readonly<Record<string, unknown>>;
  readonly exception?: { readonly values: readonly SentryExceptionValue[] };
  readonly message?: { readonly formatted: string };
}

/**
 * Sentry's envelope: three newline-delimited parts. The trailing newline is required.
 * Exported because the test asserts against the exact bytes that go on the wire — an
 * envelope that Sentry rejects is indistinguishable, from inside the browser, from one
 * it accepted.
 */
export function buildEnvelope(event: SentryEvent, sentAtIso: string): string {
  const envelopeHeader = JSON.stringify({ event_id: event.event_id, sent_at: sentAtIso });
  const itemHeader = JSON.stringify({ type: 'event', content_type: 'application/json' });
  return `${envelopeHeader}\n${itemHeader}\n${JSON.stringify(event)}\n`;
}

/**
 * Parse a V8 stack (Chrome, Android WebView — every runtime this ships to) into Sentry
 * frames, oldest first. Lines that do not match are not guessed at: the raw stack travels
 * in `extra.raw_stack` so nothing is lost when the parse comes up short.
 */
export function parseStack(stack: string): readonly SentryStackFrame[] {
  const frames: SentryStackFrame[] = [];
  for (const line of stack.split('\n')) {
    const match = /^\s*at (?:(.+?) \()?(.+?):(\d+):(\d+)\)?\s*$/.exec(line);
    if (match === null) continue;
    const [, fnName, filename, lineNo, colNo] = match;
    if (filename === undefined || lineNo === undefined || colNo === undefined) continue;
    frames.push({
      filename,
      function: fnName ?? '<anonymous>',
      lineno: Number.parseInt(lineNo, 10),
      colno: Number.parseInt(colNo, 10),
      // Everything we bundle is ours; node_modules chunks are not distinguishable after
      // a Vite build, so this is a claim about the bundle, not about a module boundary.
      in_app: true,
    });
  }
  return frames.reverse();
}

// ---------------------------------------------------------------------------
// Reporter
// ---------------------------------------------------------------------------

export interface CaptureContext {
  readonly level?: SentryLevel;
  readonly tags?: Readonly<Record<string, string>>;
  readonly extra?: Readonly<Record<string, unknown>>;
  readonly mechanism?: { readonly type: string; readonly handled: boolean };
}

export interface ReporterStats {
  /** Envelopes the transport accepted. */
  readonly sent: number;
  /** Envelopes the transport rejected — a 4xx/5xx from ingest, or the network. */
  readonly failed: number;
  /** Captures that never became an envelope because the reporter is disabled or capped. */
  readonly dropped: number;
  /** Captures suppressed as a repeat of a fingerprint already sent this session. */
  readonly deduped: number;
}

/**
 * Why a reporter is not sending. `null` only when it is.
 */
export type DisabledReason =
  | 'no-dsn'
  | 'invalid-dsn'
  | 'no-environment'
  | 'no-transport'
  | null;

export interface ErrorReporter {
  readonly kind: 'sentry' | 'disabled';
  readonly enabled: boolean;
  readonly disabledReason: DisabledReason;
  /** Human-readable form of `disabledReason`, or `null` when enabled. */
  readonly disabledDetail: string | null;
  captureException(error: unknown, context?: CaptureContext): void;
  captureMessage(message: string, context?: CaptureContext): void;
  stats(): ReporterStats;
  /** Resolves once every in-flight envelope has settled. */
  flush(): Promise<void>;
  uninstall(): void;
}

/**
 * Where the two global handlers are installed. Written against the untyped `EventTarget`
 * overload rather than `WindowEventMap` so that `window` satisfies it with no cast and a test
 * fake satisfies it with no DOM.
 */
export type GlobalErrorListener = (event: Event) => void;

export interface GlobalErrorSource {
  addEventListener(type: string, listener: GlobalErrorListener): void;
  removeEventListener(type: string, listener: GlobalErrorListener): void;
}

export interface ConsoleLike {
  warn(message: string): void;
  error(message: string): void;
}

export interface ErrorReporterOptions {
  /** Which bundle this is: `advertiser`, `host`, `admin`, `player`. Becomes the Sentry logger and the `dw.app` tag. */
  readonly app: string;
  /** The public DSN, or `null` when none is configured. */
  readonly dsn: string | null;
  /** `staging` / `prod`. Required alongside a DSN — see `no-environment` below. */
  readonly environment: string | null;
  /** Sentry release tag; omitted from the event when absent rather than faked. */
  readonly release?: string | null;
  /** Where the global handlers are installed. `null` installs none (tests, SSR). */
  readonly source?: GlobalErrorSource | null;
  readonly transport?: SentryTransport;
  readonly console?: ConsoleLike;
  readonly now?: () => number;
  readonly randomId?: () => string;
  /**
   * Optional ceiling on envelopes per page/boot. Undefined means uncapped; fingerprint
   * dedupe already bounds a loop that throws the same error forever, and a number here
   * would be a threshold invented in code (OWNERSHIP.md §4).
   */
  readonly maxEventsPerSession?: number;
}

const DISABLED_DETAIL: Readonly<Record<Exclude<DisabledReason, null>, string>> = {
  'no-dsn': 'no Sentry DSN is configured for this bundle',
  'invalid-dsn': 'the configured Sentry DSN could not be parsed',
  'no-environment':
    'a DSN is configured but no environment tag is; refusing to send events that cannot be told apart from production',
  'no-transport': 'no fetch implementation is available to send envelopes',
};

export function initErrorReporting(options: ErrorReporterOptions): ErrorReporter {
  const console_ = options.console ?? globalThis.console;
  const now = options.now ?? ((): number => Date.now());
  const randomId = options.randomId ?? randomHex32;
  const release = options.release ?? null;

  const stats = { sent: 0, failed: 0, dropped: 0, deduped: 0 };
  const readStats = (): ReporterStats => ({ ...stats });

  const disabled = (reason: Exclude<DisabledReason, null>, extra?: string): ErrorReporter => {
    const detail = extra === undefined ? DISABLED_DETAIL[reason] : `${DISABLED_DETAIL[reason]}: ${extra}`;
    // `no-dsn` is an ordinary local/dev state. The other three mean somebody configured
    // reporting and it is not happening, which is the failure this whole module exists to
    // avoid being silent about.
    const line = JSON.stringify({
      level: reason === 'no-dsn' ? 'warn' : 'error',
      event: 'observability.sentry.disabled',
      app: options.app,
      reason,
      detail,
    });
    if (reason === 'no-dsn') console_.warn(line);
    else console_.error(line);
    return {
      kind: 'disabled',
      enabled: false,
      disabledReason: reason,
      disabledDetail: detail,
      captureException: (): void => {
        stats.dropped += 1;
      },
      captureMessage: (): void => {
        stats.dropped += 1;
      },
      stats: readStats,
      flush: (): Promise<void> => Promise.resolve(),
      uninstall: (): void => undefined,
    };
  };

  const rawDsn = options.dsn?.trim() ?? '';
  if (rawDsn === '') return disabled('no-dsn');

  const environment = options.environment?.trim() ?? '';
  if (environment === '') return disabled('no-environment');

  let dsn: SentryDsn;
  try {
    dsn = parseSentryDsn(rawDsn);
  } catch (error) {
    return disabled('invalid-dsn', error instanceof Error ? error.message : String(error));
  }

  const fetchImpl = globalThis.fetch as typeof globalThis.fetch | undefined;
  const transport =
    options.transport ?? (fetchImpl === undefined ? null : createFetchTransport(fetchImpl));
  if (transport === null) return disabled('no-transport');

  const seen = new Set<string>();
  const inFlight = new Set<Promise<void>>();

  const send = (event: SentryEvent): void => {
    const body = buildEnvelope(event, new Date(now()).toISOString());
    const promise = transport({
      url: dsn.envelopeUrl,
      body,
      headers: { 'Content-Type': 'application/x-sentry-envelope' },
    })
      .then(() => {
        stats.sent += 1;
      })
      .catch((error: unknown) => {
        stats.failed += 1;
        // One line, no rethrow, no user-visible surface. A reporting failure must never
        // become the thing being reported (invariant 9).
        console_.warn(
          JSON.stringify({
            level: 'warn',
            event: 'observability.sentry.send_failed',
            app: options.app,
            detail: error instanceof Error ? error.message : String(error),
          }),
        );
      })
      .finally(() => {
        inFlight.delete(promise);
      });
    inFlight.add(promise);
  };

  const admit = (fingerprint: string): boolean => {
    const max = options.maxEventsPerSession;
    if (max !== undefined && stats.sent + stats.failed + inFlight.size >= max) {
      stats.dropped += 1;
      return false;
    }
    if (seen.has(fingerprint)) {
      stats.deduped += 1;
      return false;
    }
    seen.add(fingerprint);
    return true;
  };

  const baseEvent = (
    level: SentryLevel,
    context: CaptureContext | undefined,
  ): Pick<
    SentryEvent,
    'event_id' | 'timestamp' | 'platform' | 'level' | 'logger' | 'environment' | 'tags' | 'sdk'
  > => ({
    event_id: randomId(),
    timestamp: now() / 1000,
    platform: 'javascript',
    level: context?.level ?? level,
    logger: options.app,
    environment,
    tags: { 'dw.app': options.app, ...context?.tags },
    sdk: { name: SDK_NAME, version: SDK_VERSION },
  });

  const optionalParts = (
    context: CaptureContext | undefined,
    extra: Readonly<Record<string, unknown>> | null,
  ): Pick<SentryEvent, 'release' | 'extra'> => {
    const merged = { ...extra, ...context?.extra };
    return {
      ...(release === null ? {} : { release }),
      ...(Object.keys(merged).length === 0 ? {} : { extra: merged }),
    };
  };

  const captureException = (error: unknown, context?: CaptureContext): void => {
    const described = describe(error);
    const frames = described.stack === null ? [] : parseStack(described.stack);
    const fingerprint = `exception:${described.type}:${described.value}:${
      frames.length === 0 ? '' : JSON.stringify(frames[frames.length - 1])
    }`;
    if (!admit(fingerprint)) return;
    const rawStack =
      described.stack !== null && frames.length === 0 ? { raw_stack: described.stack } : {};
    send({
      ...baseEvent('error', context),
      exception: {
        values: [
          {
            type: described.type,
            value: described.value,
            mechanism: context?.mechanism ?? { type: 'generic', handled: true },
            ...(frames.length === 0 ? {} : { stacktrace: { frames } }),
          },
        ],
      },
      ...optionalParts(context, Object.keys(rawStack).length === 0 ? null : rawStack),
    });
  };

  const captureMessage = (message: string, context?: CaptureContext): void => {
    if (!admit(`message:${message}`)) return;
    send({
      ...baseEvent('info', context),
      message: { formatted: message },
      ...optionalParts(context, null),
    });
  };

  const source = options.source ?? null;
  const onError: GlobalErrorListener = (event: unknown): void => {
    const record = asRecord(event);
    const inner = record?.['error'];
    if (inner !== undefined && inner !== null) {
      captureException(inner, { mechanism: { type: 'onerror', handled: false } });
      return;
    }
    // No `error` object — a cross-origin script, or a throw of a non-Error. Everything the
    // ErrorEvent still carries is worth more than nothing.
    const message = typeof record?.['message'] === 'string' ? record['message'] : 'unknown error';
    const filename = typeof record?.['filename'] === 'string' ? record['filename'] : null;
    captureException(new Error(message), {
      mechanism: { type: 'onerror', handled: false },
      ...(filename === null ? {} : { extra: { filename } }),
    });
  };
  const onRejection: GlobalErrorListener = (event: unknown): void => {
    const record = asRecord(event);
    captureException(record?.['reason'] ?? new Error('unhandled rejection with no reason'), {
      mechanism: { type: 'onunhandledrejection', handled: false },
    });
  };

  if (source !== null) {
    source.addEventListener('error', onError);
    source.addEventListener('unhandledrejection', onRejection);
  }

  return {
    kind: 'sentry',
    enabled: true,
    disabledReason: null,
    disabledDetail: null,
    captureException,
    captureMessage,
    stats: readStats,
    flush: async (): Promise<void> => {
      while (inFlight.size > 0) await Promise.all([...inFlight]);
    },
    uninstall: (): void => {
      if (source === null) return;
      source.removeEventListener('error', onError);
      source.removeEventListener('unhandledrejection', onRejection);
    },
  };
}

// ---------------------------------------------------------------------------
// Reading the configuration a bundle was built with
// ---------------------------------------------------------------------------

export interface SentryEnvOptions {
  readonly dsn: string | null;
  readonly environment: string | null;
  readonly release: string | null;
}

/**
 * Read `<prefix>SENTRY_DSN`, `<prefix>SENTRY_ENVIRONMENT` and `<prefix>SENTRY_RELEASE` from a
 * Vite-style env record. Values are inlined at build time, so an unset key is genuinely absent
 * from the bundle rather than empty at runtime.
 *
 * The parameter is `unknown`-valued because Vite types `import.meta.env` with booleans on it
 * (`DEV`, `PROD`); anything that is not a non-empty string reads as absent.
 */
export function readSentryEnv(
  env: { readonly [key: string]: unknown },
  prefix: string,
): SentryEnvOptions {
  const read = (name: string): string | null => {
    const value = env[`${prefix}${name}`];
    if (typeof value !== 'string') return null;
    const trimmed = value.trim();
    return trimmed === '' ? null : trimmed;
  };
  return {
    dsn: read('SENTRY_DSN'),
    environment: read('SENTRY_ENVIRONMENT'),
    release: read('SENTRY_RELEASE'),
  };
}

// ---------------------------------------------------------------------------
// Internals
// ---------------------------------------------------------------------------

interface DescribedError {
  readonly type: string;
  readonly value: string;
  readonly stack: string | null;
}

function describe(error: unknown): DescribedError {
  if (error instanceof Error) {
    return {
      type: error.name === '' ? 'Error' : error.name,
      value: error.message,
      stack: typeof error.stack === 'string' ? error.stack : null,
    };
  }
  if (typeof error === 'string') return { type: 'Error', value: error, stack: null };
  const record = asRecord(error);
  if (record !== null && typeof record['message'] === 'string') {
    return {
      type: typeof record['name'] === 'string' ? record['name'] : 'Error',
      value: record['message'],
      stack: typeof record['stack'] === 'string' ? record['stack'] : null,
    };
  }
  // A thrown non-Error. Say what it actually was; do not manufacture a message.
  return { type: 'NonError', value: safeStringify(error), stack: null };
}

function asRecord(value: unknown): Readonly<Record<string, unknown>> | null {
  return typeof value === 'object' && value !== null
    ? (value as Readonly<Record<string, unknown>>)
    : null;
}

function safeStringify(value: unknown): string {
  try {
    return JSON.stringify(value) ?? String(value);
  } catch {
    return Object.prototype.toString.call(value);
  }
}

/**
 * 32 hex characters. Sentry uses the event id only to dedupe a retried envelope, so this is a
 * uniqueness requirement and not a secrecy one; `crypto.getRandomValues` is used where it
 * exists and `Math.random` is an acceptable stand-in where it does not.
 */
function randomHex32(): string {
  const bytes = new Uint8Array(16);
  const webCrypto = globalThis.crypto as { getRandomValues?: (array: Uint8Array) => Uint8Array } | undefined;
  if (typeof webCrypto?.getRandomValues === 'function') {
    webCrypto.getRandomValues(bytes);
  } else {
    for (let index = 0; index < bytes.length; index += 1) {
      bytes[index] = Math.floor(Math.random() * 256);
    }
  }
  let out = '';
  for (const byte of bytes) out += byte.toString(16).padStart(2, '0');
  return out;
}
